/* eslint-disable no-restricted-syntax -- amber tokens for warning, theme has no warning colour */
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Loader2,
  RefreshCw
} from 'lucide-react';
import Button from '@components/atoms/ui-kit/button';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import type {
  PublishFailureReason,
  PublishJob
} from '@utils/types/publishing/publish.ts';

const WARNING_ICON = '#B45309';

interface Props {
  job: PublishJob;
  onRetry: () => void;
  onReconnect: () => void;
  onPickDifferentTarget: () => void;
  onDone: () => void;
  /** When provided, running/queued state renders a Cancel button that
   *  stops the server-side job. The button is omitted during tests
   *  that don't care about cancellation. */
  onCancel?: () => void;
  isCancelling?: boolean;
}

const FAILURE_HELP_KEY: Record<PublishFailureReason, string> = {
  needs_reconnect: 'publish-failure-needs-reconnect',
  quota_exceeded: 'publish-failure-quota',
  target_gone: 'publish-failure-target-gone',
  target_rejected: 'publish-failure-target-rejected',
  network: 'publish-failure-network',
  unknown: 'publish-failure-unknown'
};

export default function StepInFlight({
  job,
  onRetry,
  onReconnect,
  onPickDifferentTarget,
  onDone,
  onCancel,
  isCancelling
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  if (job.status === 'succeeded') {
    return (
      <div
        role="status"
        className="flex flex-col items-center gap-3 py-8 text-center"
      >
        <CheckCircle2
          className="h-10 w-10"
          aria-hidden="true"
          style={{ color: getColour('success') }}
        />
        <p className="text-sm font-semibold">{t('publish-result-success')}</p>
        {job.result_url && (
          <a
            href={job.result_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm font-medium underline"
            style={{ color: getColour('activeText') }}
          >
            {t('publish-result-open')}
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
          </a>
        )}
        <Button
          variant="smoke"
          size="small"
          label={t('publish-result-close')}
          onClick={onDone}
        />
      </div>
    );
  }

  if (job.status === 'failed') {
    const reason = job.failure?.reason ?? 'unknown';
    const nextStep =
      reason === 'needs_reconnect'
        ? { key: 'publish-failure-cta-reconnect', action: onReconnect }
        : reason === 'target_gone'
          ? {
              key: 'publish-failure-cta-pick-another',
              action: onPickDifferentTarget
            }
          : { key: 'publish-failure-cta-retry', action: onRetry };

    return (
      <div
        role="alert"
        className="flex flex-col items-center gap-3 py-6 text-center"
      >
        <AlertCircle
          className="h-10 w-10"
          aria-hidden="true"
          style={{ color: WARNING_ICON }}
        />
        <p className="text-sm font-semibold">{t('publish-result-failed')}</p>
        <p
          className="max-w-md text-xs"
          style={{ color: getColour('inactiveText') }}
        >
          {job.failure?.message ?? t(FAILURE_HELP_KEY[reason])}
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            size="small"
            label={t(nextStep.key)}
            icon={<RefreshCw className="h-4 w-4" aria-hidden="true" />}
            onClick={nextStep.action}
          />
          <Button
            variant="smoke"
            size="small"
            label={t('publish-result-close')}
            onClick={onDone}
          />
        </div>
      </div>
    );
  }

  if (job.status === 'cancelled') {
    return (
      <div
        role="status"
        className="flex flex-col items-center gap-3 py-6 text-center"
      >
        <p className="text-sm font-semibold">{t('publish-result-cancelled')}</p>
        <Button
          variant="smoke"
          size="small"
          label={t('publish-result-close')}
          onClick={onDone}
        />
      </div>
    );
  }

  const percent = Math.max(0, Math.min(100, job.progress_percent ?? 0));

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-col items-center gap-3 py-8"
    >
      <Loader2
        className="h-7 w-7 animate-spin"
        aria-hidden="true"
        style={{ color: getColour('activeText') }}
      />
      <p className="text-sm font-medium">
        {job.status === 'queued'
          ? t('publish-progress-queued')
          : t('publish-progress-running')}
      </p>
      <div
        className="h-1.5 w-full max-w-sm overflow-hidden rounded-full"
        style={{ background: getColour('thirdLayer') }}
        aria-hidden="true"
      >
        <div
          className="h-full"
          style={{
            width: `${percent}%`,
            background: getColour('activeText'),
            transition: 'width 0.3s ease'
          }}
        />
      </div>
      <p className="text-xs" style={{ color: getColour('inactiveText') }}>
        {t('publish-progress-can-leave')}
      </p>
      {onCancel && (
        <Button
          variant="smoke"
          size="small"
          label={t('publish-cancel-job')}
          onClick={onCancel}
          disabled={isCancelling}
        />
      )}
    </div>
  );
}
